import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  Bus,
  MapPin,
  Users,
  DollarSign
} from 'lucide-react';
import Swal from 'sweetalert2';
import ScheduleModal from '../components/ScheduleModal';
import { scheduleApi, vehicleApi, routeApi } from '../services/api';

const ScheduleManagement = () => {
  const [schedules, setSchedules] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [masterDataLoading, setMasterDataLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterVehicle, setFilterVehicle] = useState('');
  const [filterRoute, setFilterRoute] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState('');

  // Load schedules from API
  const loadSchedules = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await scheduleApi.getSchedules();
      console.log('Schedules API response:', response);
      console.log('Schedules response type:', typeof response);
      console.log('Schedules is array:', Array.isArray(response));
      
      if (response && Array.isArray(response) && response.length > 0) {
        console.log('First schedule object:', response[0]);
        console.log('First schedule keys:', Object.keys(response[0]));
        console.log('First schedule ScheduleID:', response[0].ScheduleID);
        console.log('First schedule ScheduleName:', response[0].ScheduleName);
        console.log('First schedule BusID:', response[0].BusID);
        console.log('First schedule RouteID:', response[0].RouteID);
        console.log('First schedule Vehicle:', response[0].Vehicle);
        console.log('First schedule Route:', response[0].Route);
        console.log('First schedule IsActive:', response[0].IsActive);
        console.log('First schedule BasePrice:', response[0].BasePrice);
      }
      
      setSchedules(response || []);
      setTotalCount(response?.length || 0);
      setTotalPages(1); // For now, no pagination
    } catch (err) {
      console.error('Error loading schedules:', err);
      setError('Failed to load schedules. Please try again.');
      setSchedules([]);
    } finally {
      setLoading(false);
    }
  };

  // Load vehicles and routes for filters and modal
  const loadMasterData = async () => {
    try {
      setMasterDataLoading(true);
      console.log('Loading master data...');
      
      let vehiclesArray = [];
      let routesArray = [];
      
      // Load vehicles
      try {
        console.log('Loading vehicles with activeOnly=true...');
        const vehiclesResponse = await vehicleApi.getVehicles({}, true);
        console.log('Vehicles API response:', vehiclesResponse);
        
        // Handle the response structure - vehicles are in a 'vehicles' property
        if (vehiclesResponse?.vehicles && Array.isArray(vehiclesResponse.vehicles)) {
          vehiclesArray = vehiclesResponse.vehicles;
        } else if (Array.isArray(vehiclesResponse)) {
          vehiclesArray = vehiclesResponse;
        } else {
          vehiclesArray = [];
        }
        
        console.log('Processed vehicles array:', vehiclesArray);
      } catch (vehicleErr) {
        console.error('Failed to load vehicles:', vehicleErr);
        vehiclesArray = [];
      }
      
      // Load routes
      try {
        console.log('Loading routes with activeOnly=true...');
        const routesResponse = await routeApi.getRoutes(true);
        console.log('Routes API response:', routesResponse);
        
        // Handle the response structure - routes are in a 'value' property
        if (routesResponse?.value && Array.isArray(routesResponse.value)) {
          routesArray = routesResponse.value;
        } else if (Array.isArray(routesResponse)) {
          routesArray = routesResponse;
        } else if (routesResponse && typeof routesResponse === 'object') {
          // If it's a single route object, wrap it in an array
          routesArray = [routesResponse];
        } else {
          routesArray = [];
        }
        
        console.log('Processed routes array:', routesArray);
      } catch (routeErr) {
        console.error('Failed to load routes:', routeErr);
        routesArray = [];
      }
      
      console.log('Final vehicles array:', vehiclesArray);
      console.log('Final routes array:', routesArray);
      console.log('Vehicles length:', vehiclesArray.length);
      console.log('Routes length:', routesArray.length);
      
      setVehicles(vehiclesArray);
      setRoutes(routesArray);
    } catch (err) {
      console.error('Error loading master data:', err);
      setVehicles([]);
      setRoutes([]);
    } finally {
      setMasterDataLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadMasterData();
    loadSchedules();
  }, []);

  // Apply frontend filtering for display only
  const filteredSchedules = (schedules && Array.isArray(schedules)) ? schedules.filter(schedule => {
    // Debug logging
    console.log('Filtering schedule:', schedule.scheduleID, schedule.scheduleName);
    console.log('Schedule Vehicle:', schedule.vehicle);
    console.log('Schedule Route:', schedule.route);
    
    // Only apply search filter if there's a search term
    if (searchTerm && searchTerm.trim() !== '') {
      const searchLower = searchTerm.toLowerCase();
      const vehicle = schedule.vehicle;
      const route = schedule.route;
      
      const matchesSearch = 
        (vehicle?.registrationNumber?.toLowerCase().includes(searchLower)) ||
        (route?.fromLocation?.toLowerCase().includes(searchLower)) ||
        (route?.toLocation?.toLowerCase().includes(searchLower)) ||
        (schedule.scheduleName?.toLowerCase().includes(searchLower));
      
      if (!matchesSearch) return false;
    }
    
    // Only apply status filter if there's a status selected
    if (filterStatus && filterStatus !== '') {
      if (schedule.isActive !== (filterStatus === 'active')) return false;
    }
    
    // Only apply vehicle filter if there's a vehicle selected
    if (filterVehicle && filterVehicle !== '') {
      if (schedule.busID !== parseInt(filterVehicle)) return false;
    }
    
    // Only apply route filter if there's a route selected
    if (filterRoute && filterRoute !== '') {
      if (schedule.routeID !== parseInt(filterRoute)) return false;
    }
    
    return true;
  }) : [];

  // Get vehicle info for display
  const getVehicleInfo = (busId) => {
    return vehicles.find(v => v.id === busId) || { registrationNumber: 'Unknown Vehicle' };
  };

  // Get route info for display
  const getRouteInfo = (routeId) => {
    return routes.find(r => r.routeID === routeId) || { fromLocation: 'Unknown', toLocation: 'Unknown' };
  };

  // Handle edit schedule
  const handleEditSchedule = (schedule) => {
    setEditingSchedule(schedule);
    setShowModal(true);
  };

  // Handle save schedule (create or update)
  const handleSaveSchedule = async (scheduleData) => {
    try {
      if (editingSchedule) {
        // Update existing schedule
        await scheduleApi.updateSchedule(editingSchedule.scheduleID, scheduleData);
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Schedule updated successfully.',
          timer: 2000,
          showConfirmButton: false
        });
      } else {
        // Create new schedule
        await scheduleApi.createSchedule(scheduleData);
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Schedule created successfully.',
          timer: 2000,
          showConfirmButton: false
        });
      }
      
      // Reload schedules and close modal
      await loadSchedules();
      setShowModal(false);
      setEditingSchedule(null);
    } catch (err) {
      console.error('Error saving schedule:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: err.message || 'Failed to save schedule. Please try again.',
        confirmButtonText: 'OK'
      });
    }
  };

  // Handle toggle schedule status
  const handleToggleStatus = async (scheduleId) => {
    // Find the schedule to get current status
    const schedule = schedules.find(s => s.scheduleID === scheduleId);
    if (!schedule) return;

    const isCurrentlyActive = schedule.isActive;
    const action = isCurrentlyActive ? 'inactive' : 'active';
    const actionText = isCurrentlyActive ? 'inactivate' : 'activate';

    const result = await Swal.fire({
      icon: 'warning',
      title: `Mark as ${action.charAt(0).toUpperCase() + action.slice(1)}?`,
      text: `Are you sure you want to ${actionText} this schedule?`,
      showCancelButton: true,
      confirmButtonColor: isCurrentlyActive ? '#f59e0b' : '#10b981',
      cancelButtonColor: '#6b7280',
      confirmButtonText: `Yes, ${actionText} it!`,
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await scheduleApi.toggleStatus(scheduleId);
        await loadSchedules();
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: `Schedule has been marked as ${action} successfully.`,
          timer: 2000,
          showConfirmButton: false
        });
      } catch (err) {
        console.error('Error toggling schedule status:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: err.message || 'Failed to update schedule status. Please try again.',
          confirmButtonText: 'OK'
        });
      }
    }
  };



  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setFilterStatus('');
    setFilterVehicle('');
    setFilterRoute('');
  };

  if (loading || masterDataLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">
            {loading ? 'Loading schedules...' : 'Loading master data...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Schedule Management</h1>
              <p className="text-gray-600">Manage your bus schedules and routes</p>
            </div>
            <button
              onClick={() => setShowModal(true)}
              className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Schedule
            </button>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search by vehicle, route, or schedule name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            {/* Vehicle Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Vehicle</label>
              <select
                value={filterVehicle}
                onChange={(e) => setFilterVehicle(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                disabled={masterDataLoading}
              >
                <option value="">All Vehicles</option>
                {!masterDataLoading && Array.isArray(vehicles) && vehicles.length > 0 ? (
                  vehicles.map(vehicle => (
                    <option key={vehicle.id} value={vehicle.id}>
                      {vehicle.registrationNumber}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>No vehicles available</option>
                )}
              </select>
            </div>

            {/* Route Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Route</label>
              <select
                value={filterRoute}
                onChange={(e) => setFilterRoute(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                disabled={masterDataLoading}
              >
                <option value="">All Routes</option>
                {!masterDataLoading && Array.isArray(routes) && routes.length > 0 ? (
                  routes.map(route => (
                    <option key={route.routeID} value={route.routeID}>
                      {route.fromLocation} → {route.toLocation}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>No routes available</option>
                )}
              </select>
            </div>
          </div>

          {/* Filter Actions */}
          <div className="flex justify-between items-center mt-4">
            <div className="text-sm text-gray-600">
              Showing {filteredSchedules.length} of {schedules.length} schedules
            </div>
            <button
              onClick={clearFilters}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Clear all filters
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <XCircle className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Schedules Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Route & Vehicle
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pricing & Availability
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredSchedules.map((schedule) => {
                  // Use the Vehicle and Route data that's already included in the schedule response
                  const vehicle = schedule.vehicle || { registrationNumber: 'Unknown Vehicle' };
                  const route = schedule.route || { fromLocation: 'Unknown', toLocation: 'Unknown' };
                  
                  // Debug logging
                  console.log('Rendering schedule:', schedule.scheduleID);
                  console.log('Vehicle data:', vehicle);
                  console.log('Route data:', route);
                  
                  return (
                    <tr key={schedule.scheduleID} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-4 px-6">
                        <div className="space-y-3">
                          <div className="flex items-center space-x-2">
                            <MapPin className="w-4 h-4 text-gray-400" />
                            <div className="text-sm">
                              <span className="font-medium">{route.fromLocation}</span>
                              <span className="text-gray-400 mx-2">→</span>
                              <span className="font-medium">{route.toLocation}</span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Bus className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-600">{vehicle.registrationNumber}</span>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="space-y-1">
                          <div className="text-sm text-gray-600">
                            <span className="font-semibold text-green-600">Rs {schedule.basePrice?.toFixed(2) || '0.00'}</span>
                          </div>
                          <div className="text-sm text-gray-500">
                            {schedule.scheduleDates?.length || 0} dates configured
                          </div>
                          {schedule.scheduleDates && schedule.scheduleDates.length > 0 && (() => {
                            // Filter dates: show all future dates + previous 14 days
                            const today = new Date();
                            today.setHours(0, 0, 0, 0); // Reset time to start of day
                            
                            const fourteenDaysAgo = new Date(today);
                            fourteenDaysAgo.setDate(today.getDate() - 14);
                            
                            const filteredDates = schedule.scheduleDates.filter(dateObj => {
                              const date = new Date(dateObj.date);
                              date.setHours(0, 0, 0, 0); // Reset time to start of day
                              
                              // Include if date is in the future OR within last 14 days
                              return date >= fourteenDaysAgo;
                            });
                            
                            // Sort dates chronologically
                            filteredDates.sort((a, b) => {
                              return new Date(a.date) - new Date(b.date);
                            });
                            
                            return (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {filteredDates.map((date, index) => {
                                  const dateObj = new Date(date.date);
                                  const isPast = dateObj < today;
                                  
                                  return (
                                    <span
                                      key={index}
                                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                        isPast 
                                          ? 'bg-gray-100 text-gray-600' 
                                          : 'bg-blue-100 text-blue-800'
                                      }`}
                                      title={dateObj.toLocaleDateString('en-US', { 
                                        weekday: 'short',
                                        year: 'numeric',
                                        month: 'short', 
                                        day: 'numeric'
                                      })}
                                    >
                                      {dateObj.toLocaleDateString('en-US', { 
                                        month: 'short', 
                                        day: 'numeric'
                                      })}
                                    </span>
                                  );
                                })}
                                {filteredDates.length < schedule.scheduleDates.length && (
                                  <span
                                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-700"
                                    title={`Showing ${filteredDates.length} of ${schedule.scheduleDates.length} dates (future dates + last 14 days)`}
                                  >
                                    +{schedule.scheduleDates.length - filteredDates.length} more
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          schedule.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {schedule.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditSchedule(schedule)}
                            className="text-blue-600 hover:text-blue-700 p-2 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit Schedule"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleToggleStatus(schedule.scheduleID)}
                            className={`p-2 rounded-lg transition-colors ${
                              schedule.isActive 
                                ? 'text-orange-600 hover:text-orange-700 hover:bg-orange-50' 
                                : 'text-green-600 hover:text-green-700 hover:bg-green-50'
                            }`}
                            title={schedule.isActive ? 'Mark as Inactive' : 'Mark as Active'}
                          >
                            {schedule.isActive ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {(!filteredSchedules || filteredSchedules.length === 0) && !loading && (
            <div className="text-center py-12">
              <Calendar className="mx-auto h-16 w-16 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {(!schedules || schedules.length === 0) ? 'No schedules in database' : 'No schedules match your filters'}
              </h3>
              <p className="text-gray-500">
                {(!schedules || schedules.length === 0)
                  ? 'Add your first schedule to get started.' 
                  : 'Try adjusting your search criteria or clear the filters to see all schedules.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Schedule Modal */}
      <ScheduleModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingSchedule(null);
        }}
        onSave={handleSaveSchedule}
        schedule={editingSchedule}
        vehicles={vehicles}
        routes={routes}
              />
    </div>
  );
};

export default ScheduleManagement;
